// src/components/CryptoReportView.js
import React, { useRef, useState, useEffect } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import RiskBreakdown from "./RiskBreakdown";
import PsychologicalProfile from "./PsychologicalProfile";
import RecommendationCard from "./RecommendationCard";
// PortfolioAllocationChart was removed 2026-06-17 — it duplicated the
// vault breakdown already shown in StrategyRecommendations (just with a
// pie chart instead of detail cards). The richer cards won. The
// archetypeMap-based ArchetypeCard remains as the top-level sleeve view.
import StrategyRecommendations from "./StrategyRecommendations";
import RiskAssessmentDisclaimer from "./RiskAssessmentDisclaimer";
import { ARC_PRODUCTS, withTracking } from "../../config/affiliateLinks";
import ArchetypeCard from "../research/ArchetypeCard";
import { normalizeProfileType } from "./utils/cryptoCalculationUtils";

/**
 * Render a full crypto‑investor risk‑profile report and provide a one‑click PDF download.
 *
 * Dependencies (add once in your project root):
 *   npm install jspdf html2canvas
 */
const CryptoReportView = ({ results }) => {
  const reportRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Capture the report DOM node, split it across A4 pages if needed, and download as PDF.
   */
  const downloadReport = async () => {
    if (!reportRef.current || isGenerating) return;

    try {
      setIsGenerating(true);

      // 1️⃣ Capture report as a high‑resolution canvas
      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: reportRef.current.scrollWidth,
        height: reportRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png", 0.95);

      // 2️⃣ Prepare A4 portrait PDF with margins
      const pdf = new jsPDF({ 
        orientation: "p", 
        unit: "mm", 
        format: "a4",
        compress: true 
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15; // 15mm margins
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);

      // 3️⃣ Calculate scaled dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // 4️⃣ Add pages with proper positioning
      let heightLeft = imgHeight;
      let position = margin;

      // Add first page
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= contentHeight;

      // Add additional pages for overflow
      while (heightLeft > 0) {
        position = -(imgHeight - heightLeft) + margin;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      // Save with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      pdf.save(`crypto_risk_profile_${timestamp}.pdf`);

    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Color + badge styling by score. Aligned with engine's CURRENT
  // threshold bands in cryptoCalculationUtils.js (re-calibrated
  // 2026-06-20): Conservative ≤35, Moderate ≤62, Growth-Oriented ≤80,
  // Aggressive ≤93, Ultra-Aggressive ≤100. Must stay in sync — if you
  // change one, change the other.
  const getRiskProfileBadgeClass = (score) => {
    if (score <= 35) return "bg-green-100 text-green-800 border border-green-200";
    if (score <= 62) return "bg-blue-100 text-blue-800 border border-blue-200";
    if (score <= 80) return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    if (score <= 93) return "bg-orange-100 text-orange-800 border border-orange-200";
    return "bg-red-100 text-red-800 border border-red-200";
  };

  const getRiskColor = (score) => {
    if (score <= 35) return "#10b981"; // green   — Conservative
    if (score <= 62) return "#3b82f6"; // blue    — Moderate
    if (score <= 80) return "#f59e0b"; // yellow  — Growth-Oriented
    if (score <= 93) return "#f97316"; // orange  — Aggressive
    return "#ef4444";                  // red     — Ultra-Aggressive
  };

  return (
    <div className="min-h-screen bg-white/[0.03] py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8">
        {/* ===== Report Body (captured for PDF) ===== */}
        <div 
          ref={reportRef} 
          className="bg-arc-elevated rounded-lg shadow-lg overflow-hidden mx-auto space-y-6 sm:space-y-8"
          style={{ 
            minHeight: isMobile ? 'auto' : '297mm',
            maxWidth: isMobile ? '100%' : '210mm',
            margin: '0 auto',
            padding: isMobile ? '16px' : '20mm'
          }}
        >
          {/* Header */}
          <div className="text-center mb-6 sm:mb-10 border-b border-arc-line pb-4 sm:pb-8">
            <div className="p-3 sm:p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mb-4 mx-auto max-w-sm sm:max-w-none">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 leading-tight">
                Investor Risk Profile
              </h1>
              <p className="text-blue-100 text-sm sm:text-base">Detailed Analysis & Portfolio Recommendations</p>
            </div>
            <p className="text-arc-muted mt-4 text-sm sm:text-base">
              Generated on {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Overall Score Section */}
          <div className="mb-8 sm:mb-12">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-arc-ink mb-4 sm:mb-6">Your Risk Profile</h2>
              
              {/* Main Score Display */}
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 bg-white/[0.03] rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="text-center">
                  <div 
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold text-white mb-2 mx-auto"
                    style={{ backgroundColor: getRiskColor(results.totalScore) }}
                  >
                    {results.totalScore}
                  </div>
                  <span className="text-xs sm:text-sm text-arc-muted">Risk Score</span>
                </div>
                {/*
                 * Single source of truth: `results.riskProfile.type`
                 * comes straight from the scoring engine and already
                 * accounts for safety-rail behavior. The redundant
                 * subtitle that read from a separate (and inconsistent)
                 * threshold table was removed — it was the source of
                 * the "shows two different profiles" bug.
                 */}
                <div className="text-center sm:text-left">
                  <div className={`inline-block px-3 sm:px-4 py-2 rounded-lg font-semibold text-sm sm:text-base ${getRiskProfileBadgeClass(results.totalScore)}`}>
                    {/* Normalize legacy tier names at display time as a
                        safety net — even if the migration in
                        RiskAssessment.js hasn't run yet (or the saved
                        data was loaded from a different code path),
                        the displayed name is current. */}
                    {normalizeProfileType(results.riskProfile.type)}
                  </div>
                </div>
              </div>

              <p className="text-arc-muted max-w-2xl mx-auto leading-relaxed text-sm sm:text-base px-2 sm:px-0">
                {results.riskProfile.description}
              </p>
            </div>

            {/* Risk Scale Visualization

                Critical: the colored band widths are PROPORTIONAL to their
                score ranges (not flex-1 equal widths). This makes the
                marker position (left: ${score}%) and the labels always
                agree visually.

                Bands:
                  Conservative      0-35  → 35% wide
                  Moderate         36-62  → 27% wide
                  Growth-Oriented  63-80  → 18% wide
                  Aggressive       81-93  → 13% wide
                  Ultra-Aggressive 94-100 →  7% wide

                Each label sits inside the same-width container as its
                color band, so they line up under the center of their
                section. */}
            <div className="max-w-3xl mx-auto px-2 sm:px-0 mb-10">
              <div className="relative">
                {/* Score marker — ABOVE the bar.
                    Just a small colored dot pointing down at the user's
                    score position. No line, no "You" label — the bucket
                    badge above and the bar's own color cue carry the
                    meaning already.
                    Position is `left: ${score}%` along the 0-100 bar.
                    Clamped to [0,100] to handle any legacy overflow. */}
                <div className="relative h-6 mb-2">
                  <div
                    className="absolute bottom-0 transform -translate-x-1/2"
                    style={{
                      left: `${Math.min(100, Math.max(0, results.totalScore))}%`,
                    }}
                  >
                    <div
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-white shadow-lg"
                      style={{ backgroundColor: getRiskColor(results.totalScore) }}
                    />
                  </div>
                </div>

                {/* Scale bars — proportional widths */}
                <div className="flex h-6 sm:h-8 rounded-lg overflow-hidden shadow-inner bg-white/[0.08]">
                  <div style={{ width: "35%" }} className="bg-green-400 flex items-center justify-center text-xs font-medium text-white px-1 overflow-hidden">
                    <span className="hidden sm:inline truncate">Conservative</span>
                    <span className="sm:hidden truncate">Cons</span>
                  </div>
                  <div style={{ width: "27%" }} className="bg-blue-400 flex items-center justify-center text-xs font-medium text-white px-1 overflow-hidden">
                    <span className="hidden sm:inline truncate">Moderate</span>
                    <span className="sm:hidden truncate">Mod</span>
                  </div>
                  <div style={{ width: "18%" }} className="bg-yellow-400 flex items-center justify-center text-xs font-medium text-white px-1 overflow-hidden">
                    <span className="truncate">Growth</span>
                  </div>
                  <div style={{ width: "13%" }} className="bg-orange-400 flex items-center justify-center text-xs font-medium text-white px-1 overflow-hidden">
                    <span className="hidden sm:inline truncate">Aggressive</span>
                    <span className="sm:hidden truncate">Aggr</span>
                  </div>
                  <div style={{ width: "7%" }} className="bg-red-400 flex items-center justify-center text-xs font-medium text-white px-1 overflow-hidden">
                    <span className="hidden sm:inline truncate">Ultra</span>
                    <span className="sm:hidden truncate">U</span>
                  </div>
                </div>

                {/* Score range labels — each in a container matching
                    its color band's width so they line up underneath. */}
                <div className="flex text-xs text-arc-dim mt-2">
                  <div style={{ width: "35%" }} className="text-center">0-35</div>
                  <div style={{ width: "27%" }} className="text-center">36-62</div>
                  <div style={{ width: "18%" }} className="text-center">63-80</div>
                  <div style={{ width: "13%" }} className="text-center">81-93</div>
                  <div style={{ width: "7%" }} className="text-center">94-100</div>
                </div>
              </div>
            </div>
          </div>

          {/*
           * Safety overrides banner.
           *
           * Displayed when the scoring engine's safety rails flagged
           * any risk patterns (no emergency fund, high concentration +
           * unstable income, short horizon, etc.).
           *
           * For default users, `capApplied` is also true and the badge
           * above shows the capped profile. For users with the
           * `honorStatedProfile` override, this banner is informational
           * only — their profile is the score-derived one.
           */}
          {results.safetyOverrides && results.safetyOverrides.length > 0 && (
            <div className="mb-8 sm:mb-12 rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 text-base">!</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="eyebrow text-amber-300 mb-1">
                    Safety considerations
                  </p>
                  <h4 className="font-display text-base sm:text-lg font-bold text-arc-ink mb-2">
                    {results.composite?.capApplied
                      ? `Your profile was adjusted from ${normalizeProfileType(results.composite.scoreDerivedProfile)} to ${normalizeProfileType(results.composite.profile)} based on these factors:`
                      : "Your answers flagged the following risk patterns:"}
                  </h4>
                  <ul className="space-y-2 mt-3">
                    {results.safetyOverrides.map((o) => (
                      <li
                        key={o.rule}
                        className="text-xs sm:text-sm text-arc-muted leading-relaxed flex gap-2"
                      >
                        <span className="text-amber-400 flex-shrink-0">•</span>
                        <span>{o.message}</span>
                      </li>
                    ))}
                  </ul>
                  {results.composite?.capApplied && (
                    <p className="text-[11px] text-arc-dim mt-3 italic">
                      Educational note: safety rails exist to flag patterns
                      that have historically led to outsized losses. This is
                      not personalized financial advice.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/*
           * Research-tab archetype connection (Phase 5).
           * Renders only when `archetypeViewEnabled` feature flag is on.
           * Shows an illustrative model structure for the user's profile
           * and deep-links to the Analyst Ratings module filtered to the
           * user's primary risk-character bucket.
           */}
          <div className="mb-8 sm:mb-12">
            <ArchetypeCard profile={results.riskProfile?.type} />
          </div>

          {/* Strategy Recommendations */}
          <div className="mb-8 sm:mb-12">
            <StrategyRecommendations
              riskScore={results.totalScore}
              vaultAllocation={results.vaultAllocation}
              profileType={results.riskProfile?.type}
              safetyOverrides={results.safetyOverrides}
              className=""
            />
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8 sm:mb-12">
            {/* Risk Breakdown */}
            <div className="order-2 lg:order-1">
              <RiskBreakdown scores={results.scores} />
            </div>

            {/* Recommendations */}
            <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-arc-ink mb-3 sm:mb-4 flex items-center">
                  <span className="w-2 h-5 sm:h-6 bg-arc-gold rounded mr-2 sm:mr-3"></span>
                  Illustrative Strategy Notes
                </h3>
                <RecommendationCard
                  title="Trading Approach"
                  recommendations={results.riskProfile.recommendations}
                />
              </div>
              
              <RecommendationCard
                title="Psychological Insights"
                recommendations={results.riskProfile.psychologicalInsights}
              />
            </div>
          </div>

          {/* Psychological Profile */}
          <div className="mb-8 sm:mb-12">
            <PsychologicalProfile
              psychProfiles={results.psychProfiles}
              psychStrengths={results.psychStrengths || []}
              psychWeaknesses={results.psychWeaknesses || []}
            />
          </div>

          {/* ApexOne CTA — Risk Assessment results page is the highest-intent
              moment in the entire app. The user has just answered 15 deep
              questions about their goals and risk profile. This is the moment
              to offer a personalized strategy call. */}
          <div className="border-t border-arc-line pt-6 mt-8 sm:mt-12 mb-6">
            <div className="rounded-2xl border-2 border-arc-gold bg-gradient-to-br from-arc-gold/15 via-arc-gold/5 to-transparent p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  {/*
                   * Compliance-safe CTA copy.
                   *
                   * The previous version ("1-on-1 strategy plan",
                   * "deploy this allocation — which vaults, in what
                   * order, and how to size your positions for your
                   * goals") read as personalized investment advice.
                   * Reframed to focus on education, peer learning, and
                   * what the coaching program covers — none of which
                   * crosses the advice line.
                   */}
                  <p className="eyebrow text-arc-gold mb-2">Next step</p>
                  <h3 className="font-bold text-xl sm:text-2xl text-arc-ink mb-2">
                    Want to talk through your results with an ARC strategist?
                  </h3>
                  <p className="text-sm text-arc-muted leading-relaxed mb-4 max-w-xl">
                    Your assessment is a snapshot, not a roadmap. A strategist
                    can help you understand the patterns behind your profile,
                    share what students at this level typically focus on, and
                    walk you through how ARCrypto's coaching program supports
                    the path ahead.
                  </p>
                  <a
                    href={withTracking(ARC_PRODUCTS.apexone.url, "risk-assessment-results")}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="btn-tactile btn-gold inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold"
                  >
                    Book your ApexOne strategy call →
                  </a>
                </div>
                <div className="hidden sm:flex flex-shrink-0 items-center justify-center w-24 h-24 rounded-2xl bg-arc-gold/20 border border-arc-gold/40">
                  <span className="text-4xl">🎯</span>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer + acknowledgment */}
          <RiskAssessmentDisclaimer />

          <div className="text-center mt-4 sm:mt-6 text-xs text-arc-dim">
            <div className="block sm:inline">Report generated on {new Date().toLocaleDateString()}</div>
            <div className="block sm:inline sm:before:content-['•'] sm:before:mx-2">
              ARC FIRE Tracker · Risk Profile Assessment
            </div>
          </div>
        </div>

        {/* ===== Action buttons (excluded from PDF) ===== */}
        <div className="text-center mt-6 sm:mt-8 print:hidden px-4">
          <button
            onClick={downloadReport}
            disabled={isGenerating}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="hidden sm:inline">Generating PDF...</span>
                <span className="sm:hidden">Generating...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Download Report (PDF)</span>
                <span className="sm:hidden">Download PDF</span>
              </>
            )}
          </button>
          
          <p className="text-xs sm:text-sm text-arc-dim mt-3 px-2">
            <span className="hidden sm:inline">Click to generate and download your personalized crypto risk profile report</span>
            <span className="sm:hidden">Generate your personalized report</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CryptoReportView;